import React, { useCallback } from "react";

import { VALID_WALLET_LINK_STATUSES, WalletApiFetchState } from "@swiggy-private/payment-core";

import {
    isRedirectFlowSupported,
    isPhonePePaymentMethod,
    getPhonePeParams,
    getAmazonPaySdkParams,
} from "@payment-ui/helpers/Wallet";
import {
    AmazonPaySdkState,
    WalletData,
    WalletPaymentMethodData,
    WalletSpecificActions,
} from "@payment-ui/interfaces/Wallet";
import { PaymentGroupActions } from "@payment-ui/interfaces/Payment";
import { captureError } from "@swiggy-private/core-ui";

interface UseWalletActionsParams {
    methodData: WalletPaymentMethodData;
    actions: PaymentGroupActions<WalletPaymentMethodData> & WalletSpecificActions;
    enabled: boolean;
    isPayTmMethodWithLessBalance: boolean;
    fetchState: WalletApiFetchState;
    walletData: WalletData;
    isAmazonWallet: boolean;
    sdkFetchInProgress: boolean;
    amazonPaySdkStatus: AmazonPaySdkState | null;
    paymentCode: string;
    isPhonepeSdkAvailable: boolean;
    userMobile?: string;
}

interface UseWalletActionsReturn {
    onClick: (event: React.MouseEvent) => void;
    onPayClick: () => Promise<void>;
    onAddMoneyClick: (amount?: number) => void;
    onAddMoneyImpressionEvent: () => void;
}

export const useWalletActions = ({
    methodData,
    actions,
    enabled,
    isPayTmMethodWithLessBalance,
    fetchState,
    walletData,
    isAmazonWallet: _isAmazonWallet,
    sdkFetchInProgress,
    amazonPaySdkStatus,
    paymentCode,
    isPhonepeSdkAvailable,
    userMobile,
}: UseWalletActionsParams): UseWalletActionsReturn => {
    const {
        onSelect,
        onPay,
        onAddMoney,
        onAddMoneyImpression,
        onLinkWalletClick,
        linkAmazonPaySdk,
        linkWallet,
        getAmazonPayMetaId,
    } = actions;

    const onClick = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();

            // if wallet is disabled from backend or paytm with less balance
            if (!enabled || isPayTmMethodWithLessBalance) {
                return;
            }

            // if non-amazon pay wallet click
            if (fetchState !== WalletApiFetchState.SUCCESS && !_isAmazonWallet) {
                return;
            }

            // if amazon pay wallet
            if (_isAmazonWallet && sdkFetchInProgress) {
                return;
            }

            if (_isAmazonWallet && amazonPaySdkStatus && amazonPaySdkStatus.available && !amazonPaySdkStatus.linked) {
                onLinkWalletClick(methodData.name);
                linkAmazonPaySdk(methodData.name);
                return;
            }

            if (walletData.linkStatus !== VALID_WALLET_LINK_STATUSES.RETRY && !walletData.linked && !_isAmazonWallet) {
                onLinkWalletClick(methodData.name);
                linkWallet(methodData.name, walletData.isOldWalletLinkFlow);
                return;
            }

            onSelect(methodData);
        },
        [
            enabled,
            isPayTmMethodWithLessBalance,
            fetchState,
            _isAmazonWallet,
            sdkFetchInProgress,
            amazonPaySdkStatus,
            walletData.linkStatus,
            walletData.linked,
            walletData.isOldWalletLinkFlow,
            onSelect,
            methodData,
            linkAmazonPaySdk,
            onLinkWalletClick,
            linkWallet,
        ],
    );

    const onPayClick = useCallback(async (): Promise<void> => {
        let extraParams;
        if (!_isAmazonWallet && fetchState !== WalletApiFetchState.SUCCESS) {
            return;
        }

        /** Return IF wallet doesn;t support redirect flow now it has sufficient blance */
        if (!isRedirectFlowSupported(methodData) && !walletData.isWalletBalanceSufficent) {
            return;
        }

        /** Add extra params for phonpe wallet for INSTANT DEBIT OR REDIRECT */
        if (isPhonePePaymentMethod(methodData)) {
            extraParams = getPhonePeParams(isPhonepeSdkAvailable);
        }

        if (_isAmazonWallet && amazonPaySdkStatus) {
            let metaId = "";
            try {
                if (amazonPaySdkStatus.isNewSdkAvailable) {
                    metaId = (await getAmazonPayMetaId()) ?? "";
                }
            } catch (err) {
                captureError(err);
            }
            extraParams = getAmazonPaySdkParams(amazonPaySdkStatus.isBalanceSufficient, metaId ?? "");
        }

        onPay(methodData, extraParams);
    }, [
        _isAmazonWallet,
        fetchState,
        methodData,
        walletData.isWalletBalanceSufficent,
        amazonPaySdkStatus,
        onPay,
        isPhonepeSdkAvailable,
        getAmazonPayMetaId,
    ]);

    const onAddMoneyClick = useCallback(
        (amount?: number): void => {
            const mobileNumber = walletData.isOldWalletLinkFlow ? userMobile : walletData.mobileNumber;

            if (!mobileNumber || fetchState !== WalletApiFetchState.SUCCESS) {
                return;
            }

            if (walletData.isWalletBalanceSufficent) {
                return;
            }

            const _amount = amount ?? walletData.additionalBalanceRequired;

            onAddMoney({
                walletCode: paymentCode,
                mobile: mobileNumber,
                amount: _amount.toString(),
            });
        },
        [
            fetchState,
            onAddMoney,
            paymentCode,
            userMobile,
            walletData.additionalBalanceRequired,
            walletData.isOldWalletLinkFlow,
            walletData.isWalletBalanceSufficent,
            walletData.mobileNumber,
        ],
    );

    const onAddMoneyImpressionEvent = useCallback((): void => {
        onAddMoneyImpression(paymentCode);
    }, [onAddMoneyImpression, paymentCode]);

    return { onClick, onPayClick, onAddMoneyClick, onAddMoneyImpressionEvent };
};
